/**
 * Environment variable utility for Vite
 * Vite uses import.meta.env instead of process.env
 * Variables must be prefixed with VITE_ instead of REACT_APP_
 */

// Helper to get env var with fallback
const getEnv = (key, fallback = '') => {
  // Try VITE_ prefix first, then REACT_APP_ for backwards compatibility during migration
  return import.meta.env[`VITE_${key}`] ?? import.meta.env[`REACT_APP_${key}`] ?? fallback;
};

// ============================================================================
// Environment Mode
// ============================================================================

export const isDevelopment = import.meta.env.DEV;
export const isProduction = import.meta.env.PROD;
export const MODE = import.meta.env.MODE;

// ============================================================================
// Multi-Tenant Support
// ============================================================================

/**
 * Extract tenant ID from the current hostname.
 *
 * Examples:
 *   stanford.app.uni-fund.com → "stanford"
 *   app.uni-fund.com → null
 *   localhost:3000 → null (use X-Tenant-ID header in dev)
 */
export const getTenantFromHost = () => {
  const host = window.location.hostname.toLowerCase();

  // Development mode - no subdomain detection
  if (host === 'localhost' || host === '127.0.0.1') {
    // Check for dev override
    return getEnv('TENANT_ID', null);
  }

  // Get base domain from env or default
  const baseDomain = getEnv('BASE_DOMAIN', 'uni-fund.com').toLowerCase();

  // Remove base domain to extract subdomain parts
  if (host.endsWith(baseDomain)) {
    const prefix = host.slice(0, -(baseDomain.length + 1)); // +1 for the dot
    const parts = prefix ? prefix.split('.') : [];

    // Expected formats:
    // {tenant}.app.uni-fund.com → parts = ["tenant", "app"]
    // app.uni-fund.com → parts = ["app"]
    // {tenant}.uni-fund.com → parts = ["tenant"]

    if (!parts.length) return null;

    // If first part is "app" or "api", no tenant subdomain
    if (parts[0] === 'app' || parts[0] === 'api' || parts[0] === 'www') {
      return null;
    }

    return parts[0];
  }

  return null;
};

/**
 * Get the API base URL for the current tenant.
 *
 * In multi-tenant mode, constructs the tenant-specific API URL.
 * Falls back to API_BASE from env for single-tenant deployments.
 */
export const getApiBaseUrl = () => {
  // If API_BASE is explicitly set, use it
  const envApiBase = getEnv('API_BASE', '');
  if (envApiBase) {
    return envApiBase.replace(/\/+$/, '');
  }

  const tenant = getTenantFromHost();
  const baseDomain = getEnv('BASE_DOMAIN', 'uni-fund.com');

  // In development, use proxy
  if (isDevelopment) {
    return '';  // Use Vite proxy
  }

  // Multi-tenant: {tenant}.api.{baseDomain}
  if (tenant) {
    return `https://${tenant}.api.${baseDomain}`;
  }

  // Single-tenant: api.{baseDomain}
  return `https://api.${baseDomain}`;
};

// Current tenant (extracted once at app load)
export const CURRENT_TENANT = getTenantFromHost();

// ============================================================================
// API Configuration
// ============================================================================

// Use dynamic API base URL for multi-tenant support
export const API_BASE = getApiBaseUrl();
export const API_PROXY = getEnv('API_PROXY', '').replace(/\/$/, '');
export const EMBED_PROXY = getEnv('EMBED_PROXY', '') || API_PROXY;
export const SUPERSET_URL = getEnv('SUPERSET_URL', '/superset/').replace(/\/$/, '');

// ============================================================================
// Feature Flags (defaults - can be overridden by tenant config)
// ============================================================================

export const ENABLE_TUITION = getEnv('ENABLE_TUITION') === '1';
export const ENABLE_SUPERSET = getEnv('ENABLE_SUPERSET') === '1';
export const ENABLE_POSITIONS = getEnv('ENABLE_POSITIONS') === '1';
export const ENABLE_RESEARCH = getEnv('ENABLE_RESEARCH') === '1' || getEnv('ENABLE_GRANTS') === '1';
export const ENABLE_AI = getEnv('ENABLE_AI') === '1';
export const ENABLE_COURSES = getEnv('ENABLE_COURSES') === '1';
export const ENABLE_CAPITAL = getEnv('ENABLE_CAPITAL') === '1';

// Check if a feature flag is enabled
export const isFeatureEnabled = (flagKey) => {
  return getEnv(flagKey) === '1';
};

// ============================================================================
// Microsoft Teams Integration
// ============================================================================

// Azure AD App Registration credentials for Teams integration
export const TEAMS_APP_ID = getEnv('TEAMS_APP_ID', '');
export const AZURE_CLIENT_ID = getEnv('AZURE_CLIENT_ID', '');
export const AZURE_TENANT_ID = getEnv('AZURE_TENANT_ID', '');

// Enable Teams integration
export const ENABLE_TEAMS = getEnv('ENABLE_TEAMS') === '1';
